import { IAPIResponseError, IError } from '@checkout/errors';
import { IAddToCartSuccessPayload, ILineItemAddSuccessPayload, ILineItemRemoveSuccessPayload } from '@checkout/payloadTypes';
import { RootState } from '@checkout/state';
import { AxiosResponse } from 'axios';
import { Epic } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/concat';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/takeUntil';
import { concat as concat$ } from 'rxjs/observable/concat';
import { fromPromise as fromPromise$ } from 'rxjs/observable/fromPromise';
import { of as of$ } from 'rxjs/observable/of';
import ActionTypes from '../actions/actionTypes';
import { RootAction } from '../actions/actionTypes.d';
import {
  addLineItemSuccess,
  addToCartSuccess,
  closeCart,
  fetchKCOWidgetSuccess,
  openCart,
  preselectInitialValues,
  removeLineItemSuccess,
  resetErrors,
  responseError,
  setErrors } from '../reducers/checkout';
import { rest } from '../utils/apiUtils';
import convertMessages from '../utils/convertErrors';
import { logger } from '../utils/logger';
import { ingridResume, ingridSuspend, isShipwallet2, kcoResume, kcoSuspend } from '../utils/widgets';
import { AddToCartRequestAction, DecreaseQuantityRequestAction, IncreaseQuantityRequestAction } from './../actions/actionTypes.d';

export const addToCartEpic: Epic<RootAction, RootAction, RootState> = (action$, state$): Observable<RootAction> =>
  action$.ofType(ActionTypes.ITEM_ADD_REQUEST)
  .do(() => { kcoSuspend(); ingridSuspend(); })
  .mergeMap((action: AddToCartRequestAction) => fromPromise$(rest.httpPost(`/items/${action.payload.itemId}/quantity/${action.payload.quantity}`))
  .flatMap((payload: AxiosResponse<IAddToCartSuccessPayload>) => {
    kcoResume();
    ingridResume();
    if (!state$.value.checkout.token) {
      return Observable.concat([
        addToCartSuccess(payload.data),
        preselectInitialValues(payload.data)
      ]);
    }
    return of$(addToCartSuccess(payload.data));
  })
  .catch((error: IError) => {
    kcoResume();
    ingridResume();
    logger(error);
    if (error.response && error.response.data) {
      const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData);
      const responseApiErrorPayload: IAPIResponseError = {
        errors: convertedMessages,
        selection: error.response.data.selection,
        unavailable: error.response.data.unavilable,
      };
      return of$(responseError(responseApiErrorPayload));
    }
    return of$(setErrors(error));
  })
);

export const blinkCartEpic: Epic<RootAction, RootAction, RootState> = (action$, state$): Observable<RootAction> =>
  action$.ofType(ActionTypes.ITEM_ADD_SUCCESS)
    .mergeMap(() => of$(openCart())
      .concat(of$(closeCart())
        .delay(1800)
        .takeUntil(action$.ofType(ActionTypes.TOGGLE_CART_VISIBILITY))));

export const addLineItemEpic: Epic<RootAction, RootAction, RootState> = (action$, state$): Observable<RootAction> =>
  action$.ofType(ActionTypes.LINE_ITEM_ADD_REQUEST)
  .debounceTime(300)
  .do(() => {
    if (isShipwallet2(state$.value.checkout.selection.shipwallet)) {
      ingridSuspend();
    }
    kcoSuspend();
  })
  .switchMap((action: IncreaseQuantityRequestAction) => fromPromise$(
    rest.httpPost(`/lines/${action.payload.line}/quantity/${action.payload.quantity}`)
      .catch((error: IError) => {
        logger(error);
        return setErrors(error);
      }))
    .flatMap((payload: AxiosResponse<ILineItemAddSuccessPayload>) => {
      if (isShipwallet2(state$.value.checkout.selection.shipwallet)) {
        ingridResume();
      }
      kcoResume();
      if (payload.data.selection.klarnaReplaceSnippet !== undefined &&
          payload.data.selection.klarnaReplaceSnippet &&
          payload.data.selection.paymentHTML) {
        const { paymentMethod: selectedPaymentMethod } = state$.value.checkout.selection;
        return concat$(
          of$(fetchKCOWidgetSuccess(Object.assign({}, { formHtml: payload.data.selection.paymentHTML }, { selectedPaymentMethod }))),
          of$(addLineItemSuccess(payload.data))
        );
      }
      return of$(addLineItemSuccess(payload.data));
    })
  );

export const removeLineItemEpic: Epic<RootAction, RootAction, RootState> = (action$, state$): Observable<RootAction> =>
  action$.ofType(ActionTypes.LINE_ITEM_REMOVE_REQUEST)
  .debounceTime(300)
  .do(() => {
    if (isShipwallet2(state$.value.checkout.selection.shipwallet)) {
      ingridSuspend();
    }
    kcoSuspend();
  })
  .switchMap((action: DecreaseQuantityRequestAction) => fromPromise$(
    rest.httpDelete(`/lines/${action.payload.line}/quantity/${action.payload.quantity}`)
      .catch((error: IError) => {
        logger(error);
        return setErrors(error);
      }))
    .flatMap((payload: AxiosResponse<ILineItemRemoveSuccessPayload>) => {
      if (isShipwallet2(state$.value.checkout.selection.shipwallet)) {
        ingridResume();
      }
      kcoResume();
      if (payload.data.selection.klarnaReplaceSnippet !== undefined &&
        payload.data.selection.klarnaReplaceSnippet &&
        payload.data.selection.paymentHTML) {
      const { paymentMethod: selectedPaymentMethod } = state$.value.checkout.selection;
      return concat$(
        of$(fetchKCOWidgetSuccess(Object.assign({}, { formHtml: payload.data.selection.paymentHTML }, { selectedPaymentMethod }))),
        of$(removeLineItemSuccess(payload.data))
      );
    }
      return of$(removeLineItemSuccess(payload.data));
    })
  );

/*
 * Reseting errors after expiration date error in minicart (overlay didn't close after adding to cart)
 */
export const resetErrorsEpic: Epic<RootAction, RootAction, RootState> = (action$, state$): Observable<RootAction> =>
  action$.ofType(
    ActionTypes.ITEM_ADD_REQUEST,
    ActionTypes.LINE_ITEM_ADD_REQUEST
  )
  .filter(() => state$.value.checkout.errors !== undefined)
  .mergeMap((action) => of$(resetErrors()));



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/epics/items.ts